import { useEffect, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import {
	AUTO_COMPLETE_PREV_TASK,
	STAGE_START_LIST,
	TASK_REVIEW_STATUS,
	TASK_REVIEW_STATUS_NEW,
	TP_OWNER_SATISFICATION_COMMENT,
	WAS_SAMPLE_ON_TIME,
	WORK_COMPLEATE_LIST,
	TASK_PRIORITY_OPTIONS,
	CLOSE_REASON_LIST
} from '@/constants/constant';

import CustomDropdown from '@/components/CustomDropdown';
import ModalContainer from '@/components/modals/ModalContainer';
import { useGetAllBrandsQuery, useGetAllUserQuery, useGetEnumsQuery } from '@/api/commonApis';
import { useGetProductActionListQuery, useProductDevelopmentAddMutation } from '@/features/Scan/api/scanApi';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import ModalHeader from '@/components/modals/ModalHeader';
import InputField from '@/components/InputField';
import { isAdmin, replaceUnderLine } from '@/utils/Util';

const INITIAL_STATE = {
	task: null,
	rejectReason: '',
	sewingQty: 0,
	assignedTo: null,
	reviewStatus: null,
	brand: null,
	comment: '',
	alreadyCompletedTaskIds: [], // Auto complete previous task
	partialDevelopmentWorkCompletedUpto: null, //Work completed till*
	partialDevelopmentWorkStartedFrom: null, // Select starting stage,
	closedReason: null
};

const AddTaskModal = ({
	onClose,
	productIdList = [],
	onSuccess = () => {},
	onError = () => {},
	params = {},
	selectedAction = null,
	journeyType = '',
	excludeTasks = [],
	quoteRequestId,
	excludeJourneyTypes = 'COSTING'
}) => {
	const [taskValues, setTaskValues] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);

	const commentRef = useRef(null);

	useEffect(() => {
		if (selectedAction) {
			setTaskValues((prev) => ({
				...prev,
				task: selectedAction
			}));
		}
	}, [selectedAction?.id]);

	const { data: productActionList } = useGetProductActionListQuery({
		params: {
			type: 'ACTION',
			...params,
			excludeTaskValueTypes: excludeTasks.join(','),
			journeyType,
			...(excludeJourneyTypes && { excludeJourneyTypes })
		}
	});
	const [productDevelopmentAdd, { isLoading }] = useProductDevelopmentAddMutation();
	const { data: userList } = useGetAllUserQuery({
		params: { page: 0, size: 1000 }
	});
	const { data: fitTypes } = useGetEnumsQuery({ path: 'info.nitex.app.product.SampleDevelopmentType' });

	const handleChange = (value, name) => {
		setTaskValues((prev) => ({
			...prev,
			[name]: value
		}));
		setErrors((prev) => ({
			...prev,
			[name]: ''
		}));
	};

	const { data: brandsList } = useGetAllBrandsQuery({
		params: { page: 0, size: 1000 }
	});

	const isValidFields = () => {
		const newErrors = {};
		if (!taskValues?.task?.taskValueType) {
			newErrors.task = 'Task name is Required.';
		}
		if (taskValues?.reviewStatus?.id === 'REJECTED' && !taskValues?.rejectReason) {
			newErrors.rejectReason = 'Reject reason is Required.';
		}
		if (isSewingCompleted() && !taskValues?.assignedTo) {
			newErrors.assignedTo = 'Sewing completed by is required.';
		}
		if (isSewingCompleted() && !taskValues?.sewingQty) {
			newErrors.sewingQty = 'Sewing quantity is required.';
		}
		if ((isPresentedToTheBuyer() || isBuyerInitiallySelected()) && !taskValues?.brand) {
			newErrors.brand = 'Brand name is required.';
		}
		if (isSampleReviewByTP() && !taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value) {
			newErrors.tpOwnerCommentSampleDeliveredOnTime = 'Delivery timeline is required.';
		}
		if (isSampleReviewByTP() && !taskValues?.tpOwnerSatisfactionComment?.value) {
			newErrors.tpOwnerSatisfactionComment = 'Comment is required.';
		}
		if (
			(isPartialDevCompleated() || isPartialFactoryDev()) &&
			!taskValues?.partialDevelopmentWorkCompletedUpto?.value
		) {
			newErrors.partialDevelopmentWorkCompletedUpto = 'Stage name is required.';
		}
		if (isSetPriority() && !taskValues?.priorityType?.value) {
			newErrors.priorityType = 'Priority type is required.';
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!isValidFields()) {
			return;
		}

		const requestBody = {
			productIdList,
			productActionId: taskValues?.task?.id,
			taskValueType: taskValues?.task?.taskValueType?.constant,
			brandId: taskValues?.brand?.id,
			sampleReviewStatus: taskValues?.reviewStatus?.id,
			sewingQuantity: taskValues?.sewingQty,
			rejectionReason: taskValues?.rejectReason
				? taskValues?.rejectReason
				: taskValues?.closedReason?.id || taskValues?.closedReason?.value,
			assignedToId: taskValues?.assignedTo?.id,
			note: taskValues?.comment,
			orderSampleDevelopmentType: taskValues?.fitTypes?.id,
			alreadyCompletedTaskIds: taskValues?.alreadyCompletedTaskIds?.map((item) => item?.id) || null,
			partialDevelopmentWorkCompletedUpto:
				taskValues?.partialDevelopmentWorkCompletedUpto?.value || null,
			partialDevelopmentWorkStartedFrom: taskValues?.partialDevelopmentWorkStartedFrom?.value || null,
			tpOwnerCommentSampleDeliveredOnTime:
				taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value || null,
			tpOwnerSatisfactionComment: taskValues?.tpOwnerSatisfactionComment?.value || null,
			taskPriority: taskValues?.priorityType?.id,
			quoteRequestId
		};

		const filteredRequestBody = Object.fromEntries(
			Object.entries(requestBody).filter(
				([, value]) =>
					value !== null &&
					value !== undefined &&
					value !== '' &&
					value !== 0 &&
					!(Array.isArray(value) && value.length === 0)
			)
		);

		productDevelopmentAdd({ body: filteredRequestBody })
			.unwrap()
			.then((res) => {
				if (!res?.success) {
					if (productIdList?.length === 1) {
						const payload = { ...res?.payload };
						toastError(Object.values(payload)[0] || 'Something went wrong!');
					} else {
						toastError(res?.message || 'Something went wrong!');
					}

					const ids = Object.keys(res?.payload);
					onError(ids?.map((item) => parseInt(item)));
					onClose();
					return;
				}

				onSuccess();
				onError([]);
				toastSuccess('Updated successfully');
				onClose();
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const isSewingCompleted = () => taskValues?.task?.taskValueType?.constant === 'SAMPLE_SEWING';

	const isQCReview = () =>
		taskValues?.task?.taskValueType?.constant === 'BUYER_SAMPLE_REVIEW' ||
		taskValues?.task?.taskValueType?.constant === 'SAMPLE_REVIEW_BY_PD' ||
		taskValues?.task?.taskValueType?.constant === 'FACTORY_DEVELOPMENT_REVIEW_BY_GT' ||
		taskValues?.task?.taskValueType?.constant === 'IN_HOUSE_DEVELOPMENT_REVIEW_BY_GT' ||
		taskValues?.task?.taskValueType?.constant === 'IN_HOUSE_DEVELOPMENT_REVIEW_BY_BUYER';

	const isSampleReviewedByTechnical = () =>
		taskValues?.task?.taskValueType?.constant === 'SAMPLE_REVIEW_QC';

	const isPresentedToTheBuyer = () => taskValues?.task?.taskValueType?.constant === 'PRESENTED_TO_BUYER';

	const isBuyerInitiallySelected = () =>
		taskValues?.task?.taskValueType?.constant === 'SELECTED_BY_BUYER' ||
		taskValues?.task?.taskValueType?.constant === 'CONFIRMED_BY_BUYER';

	const isRejected = () => isSampleReviewedByTechnical();
	const isOrderSampleReview = () =>
		taskValues?.task?.taskValueType?.constant === 'ORDER_SAMPLE_REVIEW' ||
		taskValues?.task?.taskValueType?.constant === 'SHIPMENT_SAMPLE__REVIEW' ||
		taskValues?.task?.taskValueType?.constant === 'ORDER_FIT_SAMPLE_REVIEW_BY_GT' ||
		taskValues?.task?.taskValueType?.constant === 'ORDER_PP_SAMPLE_REVIEW_BY_GT';

	const isPartialDevCompleated = () =>
		taskValues?.task?.taskValueType?.constant === 'PARTIAL_IN_HOUSE_SAMPLE_DEVELOPMENT';

	const isInHouseSampleDev = () =>
		taskValues?.task?.taskValueType?.constant === 'IN_HOUSE_SAMPLE_DEVELOPMENT_START';

	const isPartialFactoryDev = () =>
		taskValues?.task?.taskValueType?.constant === 'PARTIAL_FACTORY_SAMPLE_DEVELOPMENT';

	const isSampleReviewByTP = () => taskValues?.task?.taskValueType?.constant === 'SAMPLE_REVIEW_TP_OWNER';

	const reviewStatusFilter = () => {
		return [
			'SHIPMENT_SAMPLE_REVIEW',
			'ORDER_FIT_SAMPLE_REVIEW_BY_GT',
			'ORDER_PP_SAMPLE_REVIEW_BY_GT',
			'FACTORY_DEVELOPMENT_REVIEW_BY_GT'
		].includes(taskValues?.task?.taskValueType?.constant);
	};
	const isSetPriority = () => taskValues?.task?.taskValueType?.constant === 'TASK_PRIORITY_UPDATE';

	const isClosedReason = () =>
		taskValues?.task?.taskValueType?.constant === 'IN_HOUSE_DEVELOPMENT_CLOSED_WITHOUT_ORDER' ||
		taskValues?.task?.taskValueType?.constant === 'FACTORY_DEVELOPMENT_CLOSED_WITHOUT_ORDER' ||
		taskValues?.task?.taskValueType?.constant === 'COSTING_CLOSED_WITHOUT_ORDER';

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="update-sample-and-sales-modal">
				<div className="popup-wrapper width-420-modal shadow-16dp">
					<div className="common-popup update-sample-and-sales-modal">
						<ModalHeader title="Update task" onClose={onClose} />
						<div className="common-popup-body">
							<div className="row">
								<div className="col-md-12 pr-2">
									<div className="form-group">
										<label htmlFor="task">Select task*</label>
										<CustomDropdown
											items={productActionList?.map((item) => ({
												...item,
												value: item?.name
											}))}
											title="Select task"
											selectedItem={taskValues?.task?.name}
											onItemClick={(item) => handleChange(item, 'task')}
										/>
										{errors?.task && <span className="error">{errors?.task}</span>}
									</div>
								</div>
								{isSampleReviewByTP() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Was the sample delivered on time?*</label>
											<CustomDropdown
												items={WAS_SAMPLE_ON_TIME}
												title="Select"
												selectedItem={
													taskValues?.tpOwnerCommentSampleDeliveredOnTime?.value
												}
												onItemClick={(item) =>
													handleChange(item, 'tpOwnerCommentSampleDeliveredOnTime')
												}
											/>
											{errors?.tpOwnerCommentSampleDeliveredOnTime && (
												<span className="error">
													{errors?.tpOwnerCommentSampleDeliveredOnTime}
												</span>
											)}
										</div>
									</div>
								)}
								{isSampleReviewByTP() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">
												How satisfied are you with the sample?*
											</label>
											<CustomDropdown
												items={TP_OWNER_SATISFICATION_COMMENT}
												title="Select"
												selectedItem={taskValues?.tpOwnerSatisfactionComment?.value}
												onItemClick={(item) =>
													handleChange(item, 'tpOwnerSatisfactionComment')
												}
											/>
											{errors?.tpOwnerSatisfactionComment && (
												<span className="error">
													{errors?.tpOwnerSatisfactionComment}
												</span>
											)}
										</div>
									</div>
								)}
								{(isPartialDevCompleated() || isPartialFactoryDev()) && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="task">Work completed till*</label>
											<CustomDropdown
												items={WORK_COMPLEATE_LIST}
												title="Select"
												selectedItem={
													taskValues?.partialDevelopmentWorkCompletedUpto?.value
												}
												onItemClick={(item) =>
													handleChange(item, 'partialDevelopmentWorkCompletedUpto')
												}
											/>
											{errors?.partialDevelopmentWorkCompletedUpto && (
												<span className="error">
													{errors?.partialDevelopmentWorkCompletedUpto}
												</span>
											)}
										</div>
									</div>
								)}
								{isInHouseSampleDev() && (
									<div className="col-md-12 pr-2 ">
										<div className="form-group ">
											<label htmlFor="task">Select starting stage</label>
											<CustomDropdown
												items={STAGE_START_LIST}
												title="Select"
												selectedItem={
													taskValues?.partialDevelopmentWorkStartedFrom?.value
												}
												onItemClick={(item) => {
													handleChange(item, 'partialDevelopmentWorkStartedFrom');
													setTaskValues((prev) => ({
														...prev,
														alreadyCompletedTaskIds: AUTO_COMPLETE_PREV_TASK
													}));
												}}
											/>
											{errors?.task && <span className="error">{errors?.task}</span>}
										</div>
									</div>
								)}
								{isInHouseSampleDev() && (
									<div className="col-md-12 pr-2">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="task">Auto complete previous task</label>
											<CreatableSelect
												placeholder="Select"
												onChange={(value) =>
													setTaskValues((prev) => ({
														...prev,
														alreadyCompletedTaskIds: value
													}))
												}
												value={taskValues?.alreadyCompletedTaskIds}
												options={AUTO_COMPLETE_PREV_TASK}
												classNamePrefix="react-select"
												isMulti
												isValidNewOption={() => false}
											/>
											{errors?.task && <span className="error">{errors?.task}</span>}
										</div>
									</div>
								)}
								{/* if Task name is "sewing compleated" then show this */}
								{isSewingCompleted() && (
									<>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Sewing completed by*</label>
												<CustomDropdown
													items={userList
														?.filter(
															(user) => user?.primaryUserType === 'SEWING_MAN'
														)
														?.map((item) => ({
															...item,
															id: item?.id,
															value: item?.name
														}))}
													title="Select sewing compleated by"
													selectedItem={taskValues?.assignedTo?.name}
													onItemClick={(item) => handleChange(item, 'assignedTo')}
												/>
												{errors?.assignedTo && (
													<span className="error">{errors?.assignedTo}</span>
												)}
											</div>
										</div>
										{/* if Task name is "sewing compleated" then show this */}
										<div className="col-md-12 pr-2">
											<InputField
												placeholder="Sewing Qty"
												type="number"
												value={taskValues?.sewingQty}
												onChange={(e) => handleChange(e.target.value, 'sewingQty')}
												min={0}
												step={1}
												labelTitle="Sewing Qty*"
												errors={errors?.sewingQty}
											/>
										</div>
									</>
								)}

								{(isSampleReviewedByTechnical() || isQCReview()) && (
									<>
										{/* if Task name is "Sample reviewed by technical" then show this */}
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Review status</label>
												<CustomDropdown
													items={TASK_REVIEW_STATUS?.map((item) => ({
														...item,
														value: item?.value
													}))}
													title="Select review status"
													selectedItem={taskValues?.reviewStatus?.value}
													onItemClick={(item) => handleChange(item, 'reviewStatus')}
												/>
											</div>
										</div>
										{(isRejected() || isQCReview()) &&
											taskValues?.reviewStatus?.id === 'REJECTED' && (
												<>
													{/* if Task name is "Sample reviewed by technical and status is rejected" then show this */}
													<div className="col-md-12 pr-2">
														<InputField
															placeholder="Reject reason"
															type="text"
															value={taskValues?.rejectReason}
															onChange={(e) =>
																handleChange(e.target.value, 'rejectReason')
															}
															labelTitle="Reject reason*"
															errors={errors?.rejectReason}
														/>
													</div>
												</>
											)}
									</>
								)}
								{isClosedReason() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Select Reason</label>
											<CustomDropdown
												items={CLOSE_REASON_LIST?.map((item) => ({
													...item,
													value: item?.value
												}))}
												title="Select review status"
												selectedItem={taskValues?.closedReason?.value}
												onItemClick={(item) => handleChange(item, 'closedReason')}
												isAddNew={isAdmin()}
											/>
										</div>
									</div>
								)}
								{isOrderSampleReview() && (
									<>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Sample type</label>
												<CustomDropdown
													items={fitTypes?.map((item) => ({
														...item,
														id: item?.constant,
														value: replaceUnderLine(item?.name)
													}))}
													title="Select brand"
													selectedItem={taskValues?.fitTypes?.value}
													onItemClick={(item) => handleChange(item, 'fitTypes')}
												/>
											</div>
										</div>
										<div className="col-md-12 pr-2">
											<div className="form-group">
												<label htmlFor="brand">Review status</label>
												<CustomDropdown
													items={
														reviewStatusFilter()
															? TASK_REVIEW_STATUS_NEW
															: TASK_REVIEW_STATUS?.map((item) => ({
																	...item,
																	value: item?.value
																}))
													}
													title="Select review status"
													selectedItem={taskValues?.reviewStatus?.value}
													onItemClick={(item) => handleChange(item, 'reviewStatus')}
												/>
											</div>
										</div>
										{taskValues?.reviewStatus?.id === 'REJECTED' && (
											<div className="col-md-12 pr-2">
												<InputField
													placeholder="Reject reason*"
													type="text"
													value={taskValues?.rejectReason}
													onChange={(e) =>
														handleChange(e.target.value, 'rejectReason')
													}
													labelTitle="Reject reason*"
													errors={errors?.rejectReason}
												/>
											</div>
										)}
									</>
								)}
								{(isPresentedToTheBuyer() || isBuyerInitiallySelected()) && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Buyer brand name*</label>
											<CustomDropdown
												items={brandsList}
												title="Select brand"
												selectedItem={taskValues?.brand?.name}
												onItemClick={(item) => handleChange(item, 'brand')}
											/>
											{errors?.brand && <span className="error">{errors?.brand}</span>}
										</div>
									</div>
								)}
								{isSetPriority() && (
									<div className="col-md-12 pr-2">
										<div className="form-group">
											<label htmlFor="brand">Priority type*</label>
											<CustomDropdown
												items={TASK_PRIORITY_OPTIONS}
												title="Select priority type"
												selectedItem={taskValues?.priorityType?.value}
												onItemClick={(item) => handleChange(item, 'priorityType')}
											/>
											{/* {taskValues?.priorityType?.value && (
												<p className="mt-1">
													{taskValues?.priorityType?.description}
												</p>
											)} */}
											{errors?.priorityType && (
												<span className="error">{errors?.priorityType}</span>
											)}
										</div>
									</div>
								)}
								<div className="col-md-12 pr-2">
									<textarea
										ref={commentRef}
										className="form-control"
										placeholder="Comment"
										name="comment"
										value={taskValues?.comment}
										onChange={(e) => {
											commentRef.current.style.height = 'auto';
											commentRef.current.style.height = `${commentRef.current.scrollHeight}px`;
											handleChange(e.target.value, 'comment');
										}}
									/>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={handleSubmit}
									disabled={isLoading}
								>
									Update
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default AddTaskModal;
